import React from 'react';
import { useQuery } from '@apollo/client';
import defaultTo from 'lodash/defaultTo';
import toNumber from 'lodash/toNumber';
import uniqBy from 'lodash/uniqBy';
import { LoadingIndicator } from '@atlassian/temp-loading-indicator';
import {
  InvoiceWithLineItems,
  PaymentMethods,
  TxaCustomerBalanceDataInstance,
  InvoiceLineItem,
  RefundItem,
  CreditNoteItem,
  TaxItems,
} from '@atlassian/cssxp-types-billing';
import { FormatCurrency, getDenominatorByCurrency } from '@css-xp/base';
import { Divider } from '@css-xp/base/divider';
import { InvoicePaymentMethod, InvoiceStatus } from '@css-xp/graphtypes/src/generated-types-new';
import { fetchtxaBalanceDetails } from '../queries/fetch-customer-balances';
import { isCreditInvoiceLineItem } from '../invoice-page-utils';
import './invoice-amount-details.scss';

interface Props {
  invoice: InvoiceWithLineItems;
  canShowPromotionOnInvoice: boolean;
  isCCPInvoice: boolean;
  version?: number;
  isSupportForOverageEnabled?: boolean;
}

interface TaxSummary {
  taxSummaryLabel: string;
  tax: number;
}

export const getTotalAmountPaidLabel = (paymentMethod: PaymentMethods | null, status: string | null) => {
  if (
    paymentMethod &&
    // leaving as array in case more items needed to be added
    paymentMethod in InvoicePaymentMethod
  ) {
    if (status && [InvoiceStatus.PAID, InvoiceStatus.PAYMENT_DEFERRED].includes(status as InvoiceStatus)) {
      return 'paid';
    }
    return 'due';
  }
  return 'paid';
};

export const getTaxSummary = ({
  lineItems,
  paymentCurrency,
  isInvoice,
  isVersionTwo,
  isSupportForOverageEnabled = false,
}: {
  lineItems: InvoiceLineItem[] | RefundItem[] | CreditNoteItem[];
  paymentCurrency: string | null;
  isInvoice: boolean;
  isVersionTwo?: boolean;
  isSupportForOverageEnabled?: boolean;
}) => {
  const taxSummaryItems: TaxSummary[] = [];
  if (isSupportForOverageEnabled) {
    // @ts-ignore
    const combinedTaxItems: TaxItems[] = lineItems?.reduce(
      (combined: TaxItems[], lineItem: InvoiceLineItem | RefundItem | CreditNoteItem) => {
        // @ts-ignore
        if (!isCreditInvoiceLineItem(lineItem) && lineItem?.overage) {
          // @ts-ignore
          combined = [...combined, ...(lineItem.taxItems || []), ...(lineItem.overage?.taxItems || [])];
        } else {
          combined = [...combined, ...(lineItem.taxItems || [])];
        }
        return combined;
      },
      []
    );

    const uniqCombinedTaxItems = uniqBy(combinedTaxItems, 'id');

    uniqCombinedTaxItems?.forEach((taxItem: TaxItems) => {
      const taxSummaryKey = toNumber(taxItem.taxPercent) + '% ' + taxItem.taxAmountLabel;
      const existingSummaryItem = taxSummaryItems.find(
        (taxSummaryItem: TaxSummary) => taxSummaryItem.taxSummaryLabel === taxSummaryKey
      );
      if (existingSummaryItem) {
        existingSummaryItem.tax += toNumber(taxItem.tax);
      } else {
        taxSummaryItems.push({
          taxSummaryLabel: taxSummaryKey,
          tax: toNumber(taxItem.tax),
        });
      }
    });
  } else {
    lineItems?.forEach((lineItem: InvoiceLineItem | RefundItem | CreditNoteItem) => {
      lineItem?.taxItems?.forEach((taxItem: TaxItems) => {
        const taxSummaryKey = toNumber(taxItem.taxPercent) + '% ' + taxItem.taxAmountLabel;
        const existingSummaryItem = taxSummaryItems.find(
          (taxSummaryItem: TaxSummary) => taxSummaryItem.taxSummaryLabel === taxSummaryKey
        );
        if (existingSummaryItem) {
          existingSummaryItem.tax += toNumber(taxItem.tax);
        } else {
          taxSummaryItems.push({
            taxSummaryLabel: taxSummaryKey,
            tax: toNumber(taxItem.tax),
          });
        }
      });
    });
  }

  const totalTax =
    taxSummaryItems?.reduce((amount: number, taxItem: TaxSummary) => amount + toNumber(taxItem.tax), 0) ?? 0;
  if (!totalTax) return null;

  return (
    taxSummaryItems?.length > 0 && (
      <>
        {!isInvoice && <Divider />}
        {isVersionTwo || (
          <p data-testid="tax-summary-heading" className="tax-summary-heading">
            Tax summary
          </p>
        )}
        {taxSummaryItems.map((item: TaxSummary, index: number) => {
          if (!item.tax) return null;
          return (
            <div data-testid="tax-summary-item" className="tax-summary-item" key={index}>
              <p>{item.taxSummaryLabel}</p>
              <div className="tax-value">
                <FormatCurrency showCurrencyCode showCommas currency={paymentCurrency} value={item.tax ?? 0} />
              </div>
            </div>
          );
        })}
        {isVersionTwo || (
          <>
            <div data-testid="total-tax-summary" className="total-tax-summary">
              <p>Total tax applied</p>
              <FormatCurrency showCurrencyCode showCommas currency={paymentCurrency} value={totalTax} />
            </div>
            {isInvoice && <Divider />}
          </>
        )}
      </>
    )
  );
};

const InvoiceAmountDetails = ({
  invoice,
  canShowPromotionOnInvoice,
  isCCPInvoice,
  version,
  isSupportForOverageEnabled,
}: Props) => {
  const {
    transactionAccountId,
    subtotal,
    totalDiscount,
    paymentCurrency,
    pricingCurrency,
    total,
    taxes,
    paymentMethod,
    status,
    upgradeCredits,
    appliedBalance,
    totalPartnerMargin,
  } = invoice as any;

  const currency = defaultTo(pricingCurrency, paymentCurrency);
  const discountAdjustedSubtotal = (
    toNumber(subtotal) -
    toNumber(totalDiscount ?? 0.0) -
    toNumber(totalPartnerMargin ?? 0.0)
  ).toFixed(2);
  const totalAmountExcludingTax = toNumber(canShowPromotionOnInvoice ? discountAdjustedSubtotal : subtotal);
  const adjustedAmountFromBalance = appliedBalance ?? 0;
  const hasUpgradeCredits = toNumber(upgradeCredits) < 0;
  const { data, loading: isCustomerBalancesLoading } = useQuery(fetchtxaBalanceDetails, {
    variables: {
      id: transactionAccountId,
    },
  });

  const txaBalanceDetails = data?.transactionAccount?.transactionAccountBalanceEntity;
  const customerBalances = txaBalanceDetails?.find(
    (txaBalanceEntity: TxaCustomerBalanceDataInstance) => txaBalanceEntity.invoiceNumber === invoice.invoiceNumber
  );

  const isInvoice = true;
  const getTotalAmount = () => toNumber(total) + (appliedBalance ?? 0);
  const isVersionTwo = version === 2;

  const renderAccountBalance = (label: string, shouldRenderAsRow?: boolean) => (
    <>
      <p>{`${shouldRenderAsRow ? '' : '('}${label}${shouldRenderAsRow ? '' : ':'} `}</p>
      {isCustomerBalancesLoading ? (
        <LoadingIndicator size="small" />
      ) : (
        <span>
          {
            <FormatCurrency
              showCurrencyCode
              showCommas
              currency={currency}
              value={
                customerBalances?.endingAmount
                  ? (customerBalances.endingAmount / getDenominatorByCurrency(currency)).toFixed(2)
                  : 0
              }
            />
          }
          {shouldRenderAsRow ? null : ')'}
        </span>
      )}
    </>
  );

  const taxSummary = getTaxSummary({
    lineItems: [...invoice.lineItems, ...(invoice?.dstLineItems ?? [])],
    paymentCurrency: currency,
    isInvoice,
    isVersionTwo,
    isSupportForOverageEnabled,
  });

  const accountCreditBalanceText = 'Account credit balance';
  const remainingBalanceText = 'Remaining balance';
  const renderRemainingBalance = (label: string, shouldRenderAsRow?: boolean) => {
    return (
      <div className="remaining-balance" data-testid="remaining-account-balance">
        {isCCPInvoice && renderAccountBalance(label, shouldRenderAsRow)}
      </div>
    );
  };

  return (
    <div
      className={isVersionTwo ? 'invoice-amounts-container-v2' : 'invoice-amounts-container'}
      data-testid="invoice-amount-details"
    >
      <div className="total-amount-excl-tax">
        <p>Total amount excl. tax</p>
        <FormatCurrency
          showCurrencyCode
          showCommas
          currency={currency}
          value={totalAmountExcludingTax - Math.abs(toNumber(invoice.upgradeCredits))}
        />
      </div>
      {hasUpgradeCredits && (
        <div className="upgrade-credit">
          <p>Upgrade credit</p>
          <td>
            <FormatCurrency
              showCurrencyCode
              showCommas
              value={Math.abs(toNumber(upgradeCredits))}
              currency={currency}
            />
          </td>
        </div>
      )}
      <div className="total-tax">
        <p>Total tax</p>
        <div className="tax-value">
          <FormatCurrency showCurrencyCode showCommas currency={currency} value={taxes ?? 0} />
        </div>
      </div>
      {isVersionTwo && taxSummary}
      <div className="total-billed-amount" data-testid="total-billed-amount">
        <p>Total billed amount</p>
        <FormatCurrency
          showCurrencyCode
          showCommas
          currency={currency}
          value={totalAmountExcludingTax - Math.abs(toNumber(invoice.upgradeCredits)) + toNumber(taxes ?? 0)}
        />
      </div>
      {adjustedAmountFromBalance < 0 && (
        <div className="adjusted-account-balance" data-testid="adjusted-account-balance">
          <p>Adjusted from account balance</p>
          <div className="credit-adjusted">
            <div className="adjusted-amount">
              <span>
                <>
                  <FormatCurrency showCurrencyCode showCommas currency={currency} value={adjustedAmountFromBalance} />
                </>
              </span>
            </div>
            {isVersionTwo || renderRemainingBalance(remainingBalanceText)}
          </div>
        </div>
      )}
      {adjustedAmountFromBalance < 0 && isVersionTwo && renderRemainingBalance(remainingBalanceText, true)}
      {adjustedAmountFromBalance > 0 && (
        <div className="adjusted-account-balance" data-testid="added-account-balance">
          <p>Added to account balance</p>
          <div className="credit-adjusted">
            <div className="adjusted-amount">
              <span>
                <>
                  <FormatCurrency showCurrencyCode showCommas currency={currency} value={adjustedAmountFromBalance} />
                </>
              </span>
            </div>
            {isVersionTwo || renderRemainingBalance(accountCreditBalanceText)}
          </div>
        </div>
      )}
      {adjustedAmountFromBalance > 0 && isVersionTwo && renderRemainingBalance(accountCreditBalanceText, true)}
      <Divider className="divider-first" />
      <div className="total-amount-paid">
        <p>{`Total amount ${getTotalAmountPaidLabel(paymentMethod, status)}`}</p>
        <FormatCurrency showCurrencyCode showCommas currency={currency} value={getTotalAmount()} />
      </div>

      {isVersionTwo || (
        <>
          <Divider />
          {taxSummary}
        </>
      )}
    </div>
  );
};

export default InvoiceAmountDetails;
